import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import {
    Box,
    Grid,
    Typography,
    Button,
    Dialog
} from "@mui/material";
import { TCCreatableSelect } from 'components/TopCourtComponents/Input/TopCourtInput';
import { createSportCourtType, updateSportCourtType, getSportCategoryOptions, getCourtTypeOptions, deleteSportCourtType } from 'services/sportCourtTypeServices';
import { toast } from 'react-toastify';
import ButtonComponent from 'views/components/buttonComponent';
import BoxButtonComponent from 'views/components/boxButtonComponent';
import ConfirmDialogComponent from 'views/components/cofirmDialogComponent';
import { validation } from 'helper/Form';

export default function AddSportCategoryDialog({ merchantId, placeId, setDialog, handleClose }) {
    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)

    const [sportCategoryOptions, setSportCategoryOptions] = useState([]);
    const [courtTypeOptions, setCourtTypeOptions] = useState([]);

    const [form, setForm] = useState({
        sportTypeId: '',
        sportTypeSelected: null,
        courtTypeId: '',
        courtTypeSelected: null,
    });

    const [joi, setJOI] = useState({
        sportTypeId: "required",
        courtTypeId: "required"
    })

    useEffect(() => {
        setJOI(prev => ({
            sportTypeId: !validation.isNotEmpty(form.sportTypeId) ? "Required" : "",
            courtTypeId: !validation.isNotEmpty(form.courtTypeId) ? "Required" : "",
        }))
    }, [form])

    const loadSportCategoryOptions = async () => {
        try {
            let { data, error } = await getSportCategoryOptions();
            if (error) throw error;
            if (data) {
                const options = data.map(item => ({
                    value: item._id,
                    label: item.name
                }));
                setSportCategoryOptions(options);
            }
        } catch (error) {
            console.log(error);
        }
    }

    const loadCourtTypeOptions = async () => {
        try {
            let { data, error } = await getCourtTypeOptions({
                merchantId: null, placeId: null
            });
            if (error) throw error;
            if (data) {
                const options = data.map(item => ({
                    value: item._id,
                    label: item.name
                }));
                setCourtTypeOptions(options);
            }
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        const timeout = setTimeout(() => {
            loadSportCategoryOptions();
            loadCourtTypeOptions();
        }, 800)

        return () => clearTimeout(timeout)
    }, [setDialog.open]);

    const createOrUpdate = () => {
        setIsSaving(true);
        if (form._id) {
            update()
        } else {
            create()
        }
    }

    const create = async () => {
        let body = { ...form, merchantId: merchantId, placeId: placeId }
        try {
            let { data, error } = await createSportCourtType(body);
            if (error) throw error;
            if (data) {
                toast.success('Sport Category created successfully');
                handleClose();
                setIsSaving(false);
                clearForm()
            }
        } catch (error) {
            setIsSaving(false);
            console.log('err : ', error);
        }
    }

    const update = async () => {
        try {
            let { data, error } = await updateSportCourtType(form);
            if (error) throw error;
            if (data) {
                toast.success('Sport Category updated successfully');
                handleClose();
                setIsSaving(false);
            }
        } catch (error) {
            setIsSaving(false);
            console.log(error);
        }
    }

    const deleteData = async (id) => {
        try {
            setIsDeleting(true);
            const { data, error } = await deleteSportCourtType(id);
            if (error) throw error;

            toast.success('Sport Category deleted successfully');
            handleClose(); // misal menutup modal form atau refresh list
        } catch (err) {
            toast.error("Failed to delete");
            console.error(err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDelete = () => {
        setConfirmOpen(true); // munculkan dialog konfirmasi
    };

    const handleConfirmDelete = async () => {
        await deleteData(form._id);
        setConfirmOpen(false); // tutup dialog
    };

    const clearForm = () => {
        setForm({
            sportTypeId: '',
            sportTypeSelected: null,
            courtTypeId: '',
            courtTypeSelected: null,
        });
    }

    useEffect(() => {
        clearForm()
        if (setDialog.data) {
            setForm({
                _id: setDialog.data._id,
                sportTypeId: setDialog.data.sportType?._id,
                sportTypeSelected: {
                    value: setDialog.data.sportType?._id,
                    label: setDialog.data.sportType?.name
                },
                courtTypeId: setDialog.data.courtType?._id,
                courtTypeSelected: {
                    value: setDialog.data.courtType?._id,
                    label: setDialog.data.courtType?.name
                }
            });
        }
    }, [setDialog.data]);

    return (
        <>
            <ConfirmDialogComponent
                open={confirmOpen}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={handleConfirmDelete}
                loading={isDeleting}
                title="Delete Confirmation"
                content="Are you sure you want to delete this category?"
                confirmLabel="Delete"
                cancelLabel="Cancel"
                loadingConfirmLabel="Deleting..."
            />
            <Dialog open={setDialog.open} onClose={handleClose}>
                <Box sx={{ padding: 2, width: 'auto', maxWidth: 370 }}>
                    <Typography variant="h6" gutterBottom>
                        {!form._id ? 'Add' : 'Update'} Sport Category
                    </Typography>
                    <Box component="form" noValidate autoComplete="off">
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <TCCreatableSelect
                                    label={'Sport Category*'}
                                    options={sportCategoryOptions}
                                    value={form.sportTypeSelected}
                                    onChange={(e) => { setForm({ ...form, sportTypeId: e.value, sportTypeSelected: e }) }} inDialog={true}
                                    showAlert={joi.sportTypeId}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <TCCreatableSelect
                                    label={'Court Type*'}
                                    options={courtTypeOptions}
                                    value={form.courtTypeSelected}
                                    onChange={(e) => { setForm({ ...form, courtTypeId: e.value, courtTypeSelected: e }) }} inDialog={true}
                                    showAlert={joi.courtTypeId}
                                />
                            </Grid>
                        </Grid>
                        <BoxButtonComponent justify={form?._id ? 'space-between' : 'flex-end'} direction="row">
                            {form?._id && (
                                <ButtonComponent
                                    onClick={handleDelete}
                                    loading={isDeleting}
                                    label="Delete"
                                    loadingLabel="Deleting..."
                                    buttonColor="error"
                                    spinnerColor="error.main"
                                    textColor="#fff"
                                />
                            )}
                            <Box display="flex" gap={1}>
                                <Button variant="outlined" color="secondary" onClick={handleClose}>
                                    Cancel
                                </Button>
                                <ButtonComponent
                                    disabled={!(form.sportTypeSelected && form.courtTypeSelected)}
                                    onClick={() => createOrUpdate()}
                                    loading={isSaving}
                                    label="Save"
                                    loadingLabel="Saving..."
                                />
                            </Box>
                        </BoxButtonComponent>
                    </Box>
                </Box>
            </Dialog>
        </>
    )
}